// AHA-46375: PowerPoint places the content add-in shape at whatever geometry the insertion
// path produced, so a freshly inserted frame rarely covers the slide. Normalise it to
// full-bleed once, on mount, against the slide this frame lives on.
//
// ONCE, literally: the shape is tagged after it is handled, and a tagged shape is never
// touched again. This runs on every add-in load (there is no insert hook), so without the
// tag every file-open would drag a frame the user had deliberately moved or shrunk back to
// full-bleed. See RESIZED_TAG_KEY.
//
// Requirement sets (all checked at runtime; unsupported => clean no-op, never a throw):
// - 1.3  Shape.tags — the once-per-shape marker. Absent => we degrade to resizing on every
//        load, so it is probed per shape rather than gated on, and never fatal.
// - 1.4  Slide.shapes + Shape.type/left/top/width/height — needed to find and move the shape.
// - 1.10 presentation.pageSetup.slideWidth/slideHeight — the ONLY way to learn the canvas size.
// Geometry and pageSetup are both in points, so no unit conversion is needed.
import * as Sentry from '@sentry/vue'
import store from '@/vuex/store'
import { createDebug } from '@/utils/debug'
import { isPowerPointApiSupported, queryActiveView, readSelectedSlideId } from '@/services/office/helpers'
import { findAddinShapes } from '@/services/office/addin-shapes'

// Toggle in the browser console with: localStorage.debug = 'aha:ppt:resize-addin'
const logResize = createDebug('ppt:resize-addin')

// AHA-46375: flag + per-platform variant gate. The getter owns both halves of that
// decision (see featureToggles.module.js), so this is the single switch for the feature —
// off means the add-in frame is left exactly as PowerPoint placed it.
const isAutoResizeEnabled = () => {
  return Boolean(store.getters['featureToggles/canUsePptAutoResize'])
}

const SENTRY_CATEGORY = 'ppt-resize'
const SHAPE_GEOMETRY_API_VERSION = '1.4'
const PAGE_SETUP_API_VERSION = '1.10'
// The add-in shape can still be missing from slide.shapes on the first attempt: the frame
// boots before PowerPoint has committed the newly inserted shape to the slide.
const RESIZE_MAX_ATTEMPTS = 3
const RESIZE_RETRY_DELAY_MS = 300
// Geometry comes back as floats with sub-point noise; anything within half a point of the
// canvas already reads as full-bleed, and skipping the write keeps us from marking the
// .pptx dirty (unsaved-changes prompt) on every single add-in mount.
const GEOMETRY_EPSILON_PT = 0.5

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms) })

// There is no Office.js API for "which slide is this add-in on". getSelectedDataAsync
// (SlideRange) is the closest signal: on mount, the frame's own slide is the selected one.
// Returns the 1-based slide index, or undefined when it cannot be resolved.
const getSelectedSlideIndex = () => new Promise((resolve) => {
  readSelectedSlideId((selectedId, error, slides) => {
    if (error) {
      logResize('selected slide unavailable:', error)
      resolve(undefined)
      return
    }
    resolve(slides && slides[0] && slides[0].index)
  })
})

// PowerPoint upper-cases tag keys, so this is already in the form it comes back as.
const RESIZED_TAG_KEY = 'AHASLIDES_FILLED_SLIDE'

// Shape.tags is PowerPointApi 1.3 — below the 1.10 we already require — but a host that does
// not expose it must not break the feature, so absence reads as "not handled yet" and the
// resize degrades to its pre-tag behaviour (runs on every load) rather than throwing.
const canTagShape = (shape) => typeof shape.tags?.add === 'function'

// True once this shape has had its one pass. getItemOrNullObject never throws on a missing
// key, so a shape we have never seen resolves cleanly to isNullObject.
const isAlreadyHandled = async (context, shape) => {
  const tag = shape.tags?.getItemOrNullObject?.(RESIZED_TAG_KEY)
  if (!tag) {
    return false
  }
  tag.load('isNullObject')
  await context.sync()
  return !tag.isNullObject
}

const isFullBleed = (shape, slideWidth, slideHeight) => {
  return Math.abs(shape.left) <= GEOMETRY_EPSILON_PT
    && Math.abs(shape.top) <= GEOMETRY_EPSILON_PT
    && Math.abs(shape.width - slideWidth) <= GEOMETRY_EPSILON_PT
    && Math.abs(shape.height - slideHeight) <= GEOMETRY_EPSILON_PT
}

// One attempt. Reads the canvas size and the slide's shapes in the SAME batch as the write,
// so the resize can never run against stale/unresolved dimensions. Throws when the add-in
// shape is not on the slide yet, which is what drives the retry.
const resizeAddinShapeOnce = async (slideIndex) => {
  await window.PowerPoint.run(async (context) => {
    const { pageSetup } = context.presentation
    pageSetup.load('slideWidth,slideHeight')
    const slide = context.presentation.slides.getItemAt(slideIndex - 1)
    slide.shapes.load('items/type,items/left,items/top,items/width,items/height')
    await context.sync()

    const { slideWidth } = pageSetup
    const { slideHeight } = pageSetup
    if (!slideWidth || !slideHeight) {
      throw new Error('pageSetup returned no slide dimensions')
    }

    const addinShapes = await findAddinShapes(context, slide.shapes)
    if (!addinShapes.length) {
      throw new Error('content add-in shape not found on slide')
    }

    // We cannot tell our own frame from any other content add-in — Office.js exposes no handle
    // from an add-in to the shape hosting it — so with more than one on the slide we would be
    // picking by z-order, and could move a third party's frame inside the user's deck. Leaving
    // our own frame unresized is by far the better failure. Not retried: the count will not
    // change in 900ms, and this is a permanent property of the slide, not a boot race.
    if (addinShapes.length > 1) {
      logResize('ambiguous:', addinShapes.length, 'content add-ins on the slide, declining to guess')
      Sentry.addBreadcrumb({
        category: SENTRY_CATEGORY,
        level: 'info',
        message: 'multiple content add-ins on the slide; frame left as-is',
        data: { count: addinShapes.length, platform: window.Office?.context?.platform },
      })
      return
    }

    const [addinShape] = addinShapes
    if (await isAlreadyHandled(context, addinShape)) {
      logResize('shape already handled on an earlier load, leaving it alone')
      return
    }

    // Tag even when no geometry write is needed. The tag — not the geometry — is what makes
    // this once-only: a frame PowerPoint happened to insert full-bleed still has to be marked,
    // or the user shrinking it later would just get it snapped back on the next file open.
    const shouldTag = canTagShape(addinShape)
    const needsResize = !isFullBleed(addinShape, slideWidth, slideHeight)
    if (!needsResize && !shouldTag) {
      logResize('already full-bleed and no tag support, nothing to write')
      return
    }

    if (needsResize) {
      logResize(
        'resizing', {
          left: addinShape.left, top: addinShape.top, width: addinShape.width, height: addinShape.height,
        },
        '->', { width: slideWidth, height: slideHeight },
      )
      addinShape.left = 0
      addinShape.top = 0
      addinShape.width = slideWidth
      addinShape.height = slideHeight
    } else {
      logResize('already full-bleed, tagging so we never touch this shape again')
    }
    if (shouldTag) {
      addinShape.tags.add(RESIZED_TAG_KEY, '1')
    }
    await context.sync()
  })
}

// Resize this add-in's frame to cover the whole slide canvas. Best-effort: every failure
// path is a silent no-op with a Sentry breadcrumb — a deck must never break, and the user
// must never see an error, because we could not tidy up a shape's geometry.
// AHA-43111: the PowerPoint web host does NOT service Rich API (PowerPoint.run) request batches
// in the Slide Show (read) session — every batch comes back as "An internal error has occurred".
// Resizing there is impossible AND noisy (three failed attempts + a Sentry breadcrumb per frame),
// so skip it entirely in read view. queryActiveView is a Common API and works in the show.
const resizeAddinToFillSlide = async () => {
  if (!isAutoResizeEnabled()) {
    logResize('flag off (or platform not in the variant list), skipping')
    return
  }
  if (!window.PowerPoint || !window.PowerPoint.run) {
    return
  }
  if (await queryActiveView() === 'read') {
    logResize('read/Slide Show view -> skipping resize (Rich API unavailable in the show)')
    return
  }
  if (!isPowerPointApiSupported(SHAPE_GEOMETRY_API_VERSION)) {
    logResize('PowerPointApi', SHAPE_GEOMETRY_API_VERSION, 'unsupported, skipping')
    return
  }
  // Deliberately NOT falling back to a hardcoded 960x540: that is the 16:9 canvas, and a
  // 4:3 deck is 720x540 — the guess would push the frame 240pt off the right edge. Without
  // pageSetup there is no way to tell the two apart, so on pre-1.10 hosts we leave the
  // frame exactly as PowerPoint placed it.
  if (!isPowerPointApiSupported(PAGE_SETUP_API_VERSION)) {
    logResize('PowerPointApi', PAGE_SETUP_API_VERSION, 'unsupported (no slide dimensions), skipping')
    Sentry.addBreadcrumb({
      category: SENTRY_CATEGORY,
      level: 'info',
      message: 'pageSetup unavailable; add-in frame left as-is',
      data: { platform: window.Office?.context?.platform },
    })
    return
  }

  try {
    const slideIndex = await getSelectedSlideIndex()
    if (!slideIndex) {
      logResize('no selected slide, skipping')
      return
    }

    for (let attempt = 1; attempt <= RESIZE_MAX_ATTEMPTS; attempt += 1) {
      try {
        // Sequential by design: each attempt must observe the result of the previous one.
        // eslint-disable-next-line no-await-in-loop
        await resizeAddinShapeOnce(slideIndex)
        return
      } catch (error) {
        logResize('attempt', attempt, 'failed:', error)
        if (attempt === RESIZE_MAX_ATTEMPTS) {
          throw error
        }
        // eslint-disable-next-line no-await-in-loop
        await wait(RESIZE_RETRY_DELAY_MS)
      }
    }
  } catch (error) {
    Sentry.addBreadcrumb({
      category: SENTRY_CATEGORY,
      level: 'warning',
      message: 'failed to resize add-in frame to fill slide',
      data: {
        error: error?.message,
        platform: window.Office?.context?.platform,
      },
    })
  }
}

export { resizeAddinToFillSlide }
